import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Mail, Phone, MessageSquare, Calendar as CalendarIcon } from "lucide-react";
import emailjs from '@emailjs/browser';
import clinicPhoto1 from '../../assets/clinicPhoto1.jpg';
import clinicPhoto2 from '../../assets/clinicPhoto2.jpg';
import clinicPhoto3 from '../../assets/clinicPhoto3.jpg';
import clinicPhoto4 from '../../assets/clinicPhoto4.jpg';
import VisitUsSection from '../Common/VisitUsSection';
import AnimatedSection from '../Common/AnimatedSection';
import axios from 'axios';

const AppointmentScheduler = () => {
  // All existing state declarations remain the same
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [reason, setReason] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkAvailability, setCheckAvailability] = useState(false);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const SERVICE_ID = 'service_4nze7q9';
  const TEMPLATE_ID = 'template_gi1alls';
  const PUBLIC_KEY = 'IAPeuK6_oPHoCbqtp';

  // WhatsApp Business API configuration
  const WHATSAPP_BUSINESS_ID = '629564273567509'
  const WHATSAPP_API_URL = `https://graph.facebook.com/v22.0/${WHATSAPP_BUSINESS_ID}/messages`;
  const ACCESS_TOKEN = 'EAAN3iOI3PDsBO4gvzX9hcZCrQ7CBrK1ZB9dQCYVkggzynCSQCIDAAUZBTFTzSfgZCggdNGmulmkL5DZAHTrDvY281LZAJzUHU3smcImGL4lJEAr8MI8gIyQJvVQ4Mxj3Lsw01wtBhpRQaP9kvKH5ecPd3pTHQ4LIPRrbDSFuHANHbKnpxY1YUrghIUaZCZAk';

  // All existing constants remain the same
  const services = [
    "Regular Checkup",
    "Teeth Cleaning",
    "Root Canal",
    "Tooth Extraction",
    "Dental Implants",
    "Orthodontics",
    "Teeth Whitening"
  ];

  const allTimeSlots = [
    "09:00 AM", "09:30 AM", "10:00 AM", "10:30 AM",
    "11:00 AM", "11:30 AM", "02:00 PM", "02:30 PM",
    "03:00 PM", "03:30 PM", "04:00 PM", "04:30 PM"
  ];

  const backgroundImages = [
    clinicPhoto1, clinicPhoto2, clinicPhoto3, clinicPhoto4
  ];

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [nextImageIndex, setNextImageIndex] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);
  // Toast notification state
  const [toast, setToast] = useState({
    show: false,
    message: '',
    type: 'error' // 'error', 'success', 'warning'
  });

  // Updated brand colors based on Tailwind classes
  const brandColors = {
    primary: '#0d9488',    // Tailwind teal-600
    secondary: '#84cc16',  // Tailwind lime-500
    accent: '#334155',     // Tailwind slate-700
    light: '#ffffff',      // Tailwind white
    dark: '#1f2937'        // Tailwind gray-800
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      // Start transition
      setIsTransitioning(true);
      
      // After transition completes, update indices
      setTimeout(() => {
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % backgroundImages.length);
        setNextImageIndex((prevIndex) => (prevIndex + 1) % backgroundImages.length);
        setIsTransitioning(false);
      }, 1000); // This should match the CSS transition duration
    }, 5000); // Change image every 5 seconds

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, []);

  // All existing useEffect and functions remain the same
  useEffect(() => {
    if (checkAvailability && selectedDate) {
      setLoading(true);
      setTimeout(() => {
        const available = allTimeSlots.filter(() => Math.random() > 0.3);
        setAvailableTimeSlots(available);
        setLoading(false);
      }, 1000);
    }
  }, [selectedDate, checkAvailability]);

  const showToast = (message, type = 'error') => {
    setToast({
      show: true,
      message,
      type
    });
    
    // Auto hide the toast after 5 seconds
    setTimeout(() => {
      setToast(prev => ({ ...prev, show: false }));
    }, 5000);
  };

  // Function to send WhatsApp message
  const sendWhatsAppMessage = async (data) => {
    const localDate = new Date(`${selectedDate}T00:00:00`);
    const formattedDate = localDate.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    const response = await axios.post(
      WHATSAPP_API_URL,
      {
        messaging_product: 'whatsapp',
        recipient_type: 'individual',
        to: '16023731994',
        type: 'text',
        text: { 
          body: `
            ${name} is requesting an appointment on ${formattedDate} at ${selectedTime} for ${reason}.
            ${email && `Patients Email: ${email}`}
          ` 
        }
      },
      {
        headers: {
          'Authorization': `Bearer ${ACCESS_TOKEN}`,
          'Content-Type': 'application/json'
        }
      }
    );
  };

  const getGoogleCalendarLink = () => {
    // Parse the selected time (format: "02:30 PM")
    const parseTimeString = (timeString) => {
      const [timePart, period] = timeString.split(' ');
      let [hours, minutes] = timePart.split(':').map(num => parseInt(num, 10));

      // Convert to 24-hour format
      if (period.toUpperCase() === 'PM' && hours < 12) {
        hours += 12;
      } else if (period.toUpperCase() === 'AM' && hours === 12) {
        hours = 0;
      }

      return { hours, minutes };
    };

    // Format date for Google Calendar (YYYYMMDDTHHMMSSZ format)
    const formatGoogleCalendarDate = (date, timeString = null) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      // If time is provided, use it; otherwise use 00:00:00
      let hours = '00';
      let minutes = '00';

      if (timeString) {
        const { hours: h, minutes: m } = parseTimeString(timeString);
        hours = String(h).padStart(2, '0');
        minutes = String(m).padStart(2, '0');
      }

      return `${year}${month}${day}T${hours}${minutes}00`;
    };

    const localDate = new Date(`${selectedDate}T00:00:00`);

    // Create end time exactly 1 hour later than start time
    const endDate = new Date(localDate);
    const { hours, minutes } = parseTimeString(selectedTime);
    
    // Set the exact hours and minutes from selectedTime
    endDate.setHours(hours);
    endDate.setMinutes(minutes);
    
    // Add 1 hour for appointment duration
    endDate.setHours(endDate.getHours() + 1);
    
    // Format end time with the actual time values (1 hour later)
    const endTimeHours = endDate.getHours();
    const endTimeMinutes = endDate.getMinutes();
    const formattedEndTime = `${String(endTimeHours).padStart(2, '0')}${String(endTimeMinutes).padStart(2, '0')}00`;
    
    // Use the start time from selectedTime and calculate end time 1 hour later
    const googleCalendarStartDate = formatGoogleCalendarDate(localDate, selectedTime);
    const googleCalendarEndDate = `${endDate.getFullYear()}${String(endDate.getMonth() + 1).padStart(2, '0')}${String(endDate.getDate()).padStart(2, '0')}T${formattedEndTime}`;
    
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(`${reason} Appointment at Pristine Smile Dental`)}&dates=${googleCalendarStartDate}/${googleCalendarEndDate}&details=${encodeURIComponent(`Your dental appointment at Pristine Smile Dental.\n\nConfirmation #: PSD-${Date.now().toString().slice(-6)}\nAddress: Nilamber corporate park, 226, Vadodara, 390007\nPhone: +91 84017 49379`)}&location=${encodeURIComponent("Pristine Smile Dental, Nilamber corporate park, 226, Vadodara, 390007")}`;
  }

  const sendMail = async () => {
    const localDate = new Date(`${selectedDate}T00:00:00`);
    const formattedDate = localDate.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    const googleCalendarLink = getGoogleCalendarLink()

    const templateParams = {
      to_name: name,
      from_name: "Dr. Aesha at Pristine Smile Dental",
      practice_name: "Pristine Smile Dental",
      practice_address: "Nilamber corporate park, 226, Vadodara, 390007",
      practice_phone: "+91 84017 49379",
      practice_website: "www.pristinesmile.in",
      to_email: email,
      appointment_date: formattedDate,
      appointment_time: selectedTime,
      phone: phone,
      service: reason,
      message: `Thank you for scheduling your ${reason} appointment with Pristine Smile Dental.
              
      We have received your request for ${formattedDate} at ${selectedTime}. Our team will contact you soon to confirm the final appointment timing.
      
      If you need to reschedule or cancel your appointment, please contact us at least 24 hours in advance at +91 84017 49379.
      
      We're committed to providing you with exceptional dental care in a comfortable environment.

      Appointment Confirmation
      ----------------------
      Name: ${name}
      Email: ${email}
      Phone: ${phone}
      Service: ${reason}
      Date: ${formattedDate}
      Time: ${selectedTime}
      If you have any questions or concerns, please don't hesitate to reach out to us.
      We look forward to seeing you soon!

      Calendar Link: ${googleCalendarLink}

      Warm regards,
      Dr. Aesha Thakkar
      Pristine Smile Dental Team`,
      confirmation_number: `PSD-${Date.now().toString().slice(-6)}`,
      calendar_link: `https://pristinesmile.com/add-to-calendar?date=${selectedDate}&time=${selectedTime}&service=${encodeURIComponent(reason)}`
    };

    await emailjs.send(
      SERVICE_ID,
      TEMPLATE_ID,
      templateParams,
      PUBLIC_KEY
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
  
    try {
      // sendWhatsAppMessage();
      sendMail();
      setSubmitted(true);
    } catch (error) {
      console.error('Failed to send email:', error);
      showToast('Failed to schedule appointment. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const openWhatsApp = () => {
    const localDate = new Date(`${selectedDate}T00:00:00`);
    const formattedDate = localDate.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    
    const confirmationCode = `PSD-${Date.now().toString().slice(-6)}`;
    
    const message = `*Pristine Smile Dental - Appointment Request*%0A%0A
      *Confirmation Code:* ${confirmationCode}%0A%0A
      *Patient Details:*%0A
      Name: ${name}%0A
      Phone: ${phone}%0A
      Email: ${email}%0A%0A
      *Appointment Details:*%0A
      Service: ${reason}%0A
      Date: ${formattedDate}%0A
      Time: ${selectedTime}%0A%0A
      I would like to confirm this appointment. %0A%0A`;
  
    // Make sure to add the country code to your phone number (e.g., +1 for US)
    window.open(`https://wa.me/+918401749379?text=${message}`, '_blank');
  };

  const addToGoogleCalendar = () => {
    const url = getGoogleCalendarLink()
    window.open(url, '_blank');
  };

  return (
    <>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
          
          :root {
            --primary-color: ${brandColors.primary};
            --secondary-color: ${brandColors.secondary};
            --accent-color: ${brandColors.accent};
            --light-color: ${brandColors.light};
            --dark-color: ${brandColors.dark};
          }

          .appointment-container {
            font-family: 'Poppins', sans-serif;
            opacity: 0;
            animation: fadeIn 0.8s ease-out forwards;
          }

          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }

          .form-field {
            transition: all 0.3s ease;
          }

          .form-field:focus-within {
            transform: scale(1.02);
          }

          .input-label {
            font-weight: 500;
            letter-spacing: 0.5px;
            opacity: 0;
            animation: slideIn 0.5s ease-out forwards;
          }

          @keyframes slideIn {
            from { opacity: 0; transform: translateX(-10px); }
            to { opacity: 1; transform: translateX(0); }
          }

          .submit-button {
            transition: all 0.3s ease;
            position: relative;
            overflow: hidden;
            background: linear-gradient(45deg, var(--primary-color), var(--secondary-color));
          }

          .submit-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          }

          .submit-button:active {
            transform: translateY(0);
          }

          .success-message {
            animation: popIn 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
          }

          @keyframes popIn {
            from { transform: scale(0.8); opacity: 0; }
            to { transform: scale(1); opacity: 1; }
          }

          .action-button {
            transition: all 0.3s ease;
            position: relative;
            overflow: hidden;
          }

          .action-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          }

          .action-button::after {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            width: 0;
            height: 0;
            background: rgba(255, 255, 255, 0.2);
            border-radius: 50%;
            transform: translate(-50%, -50%);
            transition: width 0.3s, height 0.3s;
          }

          .action-button:active::after {
            width: 200px;
            height: 200px;
          }

          input, select {
            transition: all 0.3s ease;
            border: 2px solid #e2e8f0;
            background-color: rgba(255, 255, 255, 0.9);
          }

          input:focus, select:focus {
            border-color: var(--primary-color);
            box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.1);
          }

          .backdrop-blur-sm {
            backdrop-filter: blur(8px);
            transition: backdrop-filter 0.3s ease;
          }

          .backdrop-blur-sm:hover {
            backdrop-filter: blur(12px);
          }
          
          .logo-animation {
            animation: pulse 2s infinite alternate;
          }
          
          @keyframes pulse {
            from { transform: scale(1); }
            to { transform: scale(1.05); }
          }
          
          .form-container {
            background: rgba(255, 255, 255, 0.85);
            border-radius: 16px;
            box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
            backdrop-filter: blur(8px);
            border: 1px solid rgba(255, 255, 255, 0.3);
          }
          
          .background-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: linear-gradient(135deg, rgba(132, 204, 22, 0.4), rgba(13, 148, 136, 0.4));
            z-index: -1;
          }
          
          @media (max-width: 768px) {
            .form-container {
              padding: 1rem;
            }
            
            input, select {
              font-size: 16px; /* Prevents zoom on mobile */
            }
          }
          
          .tooth-svg {
            filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1));
            transition: all 0.3s ease;
          }
          
          .tooth-svg:hover {
            filter: drop-shadow(0 6px 8px rgba(0, 0, 0, 0.2));
            transform: translateY(-2px);
          }
          
          .section-header {
            position: relative;
          }
          
          .section-header::after {
            content: '';
            position: absolute;
            bottom: -8px;
            left: 50%;
            transform: translateX(-50%);
            width: 60px;
            height: 3px;
            background: linear-gradient(to right, var(--secondary-color), var(--primary-color));
            border-radius: 3px;
          }
          .toast-notification {
            position: absolute;
            top: 60px;
            right: 20px;
            min-width: 300px;
            padding: 16px;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            z-index: 1000;
            display: flex;
            align-items: center;
            justify-content: space-between;
            animation: slideInRight 0.3s forwards, fadeOut 0.3s 4.7s forwards;
          }
          
          .toast-error {
            background-color: #fee2e2;
            border-left: 4px solid #ef4444;
            color: #991b1b;
          }
          
          .toast-success {
            background-color: #dcfce7;
            border-left: 4px solid #22c55e;
            color: #166534;
          }
          
          .toast-warning {
            background-color: #fef3c7;
            border-left: 4px solid #f59e0b;
            color: #92400e;
          }
        `}
      </style>

      <div 
        className="min-h-screen py-10 px-4 relative appointment-container"
        style={{
          overflow: 'hidden'
        }}
      >
        {/* {backgroundImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 bg-cover bg-center transition-all duration-1000 ease-in-out ${
              index === currentImageIndex 
                ? 'opacity-100 z-10' 
                : index === nextImageIndex && isTransitioning 
                  ? 'opacity-100 z-0' 
                  : 'opacity-0 z-0'
            }`}
            style={{
              backgroundImage: `url(${image})`,
              transitionProperty: 'opacity, transform',
              transitionDuration: '2s',
              transform: index === currentImageIndex ? 'scale(1)' : 'scale(1.05)'
            }}
          />
        ))} */}
        
        {/* Toast Notification */}
        {toast.show && (
          <div className={`toast-notification ${toast.type === 'error' ? 'toast-error' : toast.type === 'success' ? 'toast-success' : 'toast-warning'}`}>
            <div className="flex items-center">
              {toast.type === 'error' && (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              )}
              {toast.type === 'success' && (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )}
              {toast.type === 'warning' && (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              )}
              <p>{toast.message}</p>
            </div>
            <button
              onClick={() => setToast(prev => ({ ...prev, show: false }))}
              className="ml-4 text-gray-600 hover:text-gray-800"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        )}

        <div className="background-overlay"></div>

        <div className="max-w-4xl mx-auto pt-8 pb-12">
          {/* Logo and branding */}
          <div className="flex flex-col items-center justify-center mb-8">
            {/* <div className="tooth-svg w-24 h-24 mb-2">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
                <path d="M50 0C33.8 0 20 13.8 20 30c0 7.1 2.4 13.7 6.4 18.9C31.3 56.6 38 66.5 38 80v15c0 2.8 2.2 5 5 5h14c2.8 0 5-2.2 5-5V80c0-13.5 6.7-23.4 11.6-31.1 4-5.2 6.4-11.8 6.4-18.9C80 13.8 66.2 0 50 0z" 
                fill="#ffffff" stroke="#0d9488" strokeWidth="4"/>
                <path d="M65 30c0 8.3-6.7 15-15 15s-15-6.7-15-15 6.7-15 15-15 15 6.7 15 15z" 
                fill="#84cc16"/>
              </svg>
            </div> */}
            <h1 className="text-3xl md:text-4xl font-bold text-center text-white drop-shadow-lg mb-2">Pristine Smile Dental</h1>
            <p className="text-white text-center text-lg md:text-xl opacity-90 max-w-lg mx-auto">Your smile deserves the best care</p>
          </div>

          <div className="form-container p-6 md:p-8">
            <div className="section-header text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 flex items-center justify-center gap-3">
                <CalendarIcon className="h-7 w-7 text-teal-600" />
                Schedule Your Visit
              </h2>
              <p className="text-gray-600 mt-2">Book your appointment in just a few steps</p>
            </div>

            {!submitted ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2 form-field">
                    <label className="block flex items-center gap-2 input-label">
                      <Calendar className="h-4 w-4 text-teal-600" />
                      Date
                    </label>
                    <input
                      type="date"
                      min={new Date().toISOString().split('T')[0]}
                      value={selectedDate}
                      onChange={(e) => {
                        setSelectedDate(e.target.value);
                        setSelectedTime('');
                      }}
                      className="w-full p-3 border rounded-md focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      required
                    />
                  </div>

                  <div className="space-y-2 form-field">
                    <div className="flex items-center justify-between">
                      <label className="block flex items-center gap-2 input-label">
                        <Clock className="h-4 w-4 text-teal-600" />
                        Time
                      </label>
                    </div>
                    {checkAvailability ? (
                      <select
                        value={selectedTime}
                        onChange={(e) => setSelectedTime(e.target.value)}
                        className="w-full p-3 border rounded-md focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        required
                        disabled={!selectedDate || loading}
                      >
                        <option value="">
                          {loading 
                            ? "Checking availability..." 
                            : selectedDate 
                              ? "Select a time" 
                              : "Please select a date first"}
                        </option>
                        {availableTimeSlots.map((time) => (
                          <option key={time} value={time}>{time}</option>
                        ))}
                      </select>
                    ) : (
                      <select
                        value={selectedTime}
                        onChange={(e) => setSelectedTime(e.target.value)}
                        className="w-full p-3 border rounded-md focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        required
                      >
                        <option value="">Select a time</option>
                        {allTimeSlots.map((time) => (
                          <option key={time} value={time}>{time}</option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2 form-field">
                    <label className="block input-label">Full Name</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full p-3 border rounded-md focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      required
                      placeholder="John Doe"
                    />
                  </div>

                  <div className="space-y-2 form-field">
                    <label className="block flex items-center gap-2 input-label">
                      <Mail className="h-4 w-4 text-teal-600" />
                      Email
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full p-3 border rounded-md focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="space-y-2 form-field">
                  <label className="block flex items-center gap-2 input-label">
                    <Phone className="h-4 w-4 text-teal-600" />
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full p-3 border rounded-md focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    required
                    placeholder="(123) 456-7890"
                  />
                </div>

                <div className="space-y-2 form-field">
                  <label className="block input-label">Service Required</label>
                  <select
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="w-full p-3 border rounded-md focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    required
                  >
                    <option value="">Select a service</option>
                    {services.map((service) => (
                      <option key={service} value={service}>{service}</option>
                    ))}
                  </select>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full text-white py-3 px-4 rounded-md transition-colors flex items-center justify-center gap-2 disabled:opacity-50 submit-button"
                >
                  {loading ? (
                    <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <>
                      <CalendarIcon className="h-5 w-5" />
                      Request Appointment
                    </>
                  )}
                </button>
              </form>
            ) : (
              <div className="space-y-6 success-message">
                <div className="border rounded-md p-6 bg-lime-50 border-lime-500">
                  <div className="flex items-center justify-center mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-lime-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-center mb-2 text-slate-700">Appointment Requested!</h3>
                  <p className="text-gray-700 text-center">
                    Thank you for scheduling your dental appointment! We will contact you shortly to confirm your appointment for {selectedDate} at {selectedTime}.
                  </p>
                </div>
                
                <div className="bg-white p-5 rounded-lg shadow-sm">
                  <h4 className="font-medium mb-3 text-teal-600">Appointment Details</h4>
                  <div className="space-y-2 text-gray-700">
                    <p><span className="font-medium">Name:</span> {name}</p>
                    <p><span className="font-medium">Email:</span> {email}</p>
                    <p><span className="font-medium">Phone:</span> {phone}</p>
                    <p><span className="font-medium">Service:</span> {reason}</p>
                    <p><span className="font-medium">Date & Time:</span> {selectedDate} at {selectedTime}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <button
                    onClick={openWhatsApp}
                    className="flex items-center justify-center gap-2 py-3 px-4 rounded-md text-white action-button bg-green-500"
                  >
                    <MessageSquare className="h-5 w-5" />
                    WhatsApp
                  </button>

                  <button
                    onClick={addToGoogleCalendar}
                    className="flex items-center justify-center gap-2 py-3 px-4 rounded-md text-white action-button bg-teal-600"
                  >
                    <Calendar className="h-5 w-5" />
                    Add to Calendar
                  </button>

                  <button
                    onClick={() => setSubmitted(false)}
                    className="flex items-center justify-center gap-2 py-3 px-4 rounded-md text-white action-button bg-lime-500"
                  >
                    <CalendarIcon className="h-5 w-5" />
                    New Appointment
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <AnimatedSection id='visit' delay={200}>
        <VisitUsSection />
      </AnimatedSection>
    </>
  );
};

export default AppointmentScheduler;